import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Sempre attivo
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Controlla se il consenso è già stato dato
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Mostra il banner dopo un piccolo ritardo
      const timer = setTimeout(() => {
        setShowConsent(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Applica le preferenze esistenti
      try {
        const savedConsent = JSON.parse(consentGiven);
        setPreferences(savedConsent);
        enableServices(savedConsent);
      } catch (error) {
        console.error('Errore nel parsing del consenso salvato:', error);
        localStorage.removeItem('cookieConsent');
        setShowConsent(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowConsent(false);
    
    // Qui puoi aggiungere logica per attivare i vari servizi
    enableServices(allAccepted);
  };

  const handleAcceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setShowConsent(false);
    
    enableServices(necessaryOnly);
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(savedPreferences));
    setShowConsent(false);
    setShowSettings(false);
    
    enableServices(savedPreferences);
  };

  const enableServices = (consent) => {
    // Google Analytics
    if (consent.analytics && window.gtag) {
      console.log('Analytics enabled - Google Analytics activated');
      window.gtag('consent', 'update', { 
        analytics_storage: 'granted',
        ad_storage: consent.marketing ? 'granted' : 'denied'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', { 
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
    
    if (consent.marketing) {
      console.log('Marketing cookies enabled');
      // Attiva Facebook Pixel, Google Ads, etc.
      if (window.gtag) {
        window.gtag('consent', 'update', { ad_storage: 'granted' });
      }
    }
    
    if (consent.functional) {
      console.log('Functional cookies enabled');
      // Attiva chat widgets, video embeds, etc.
    }
  };

  const togglePreference = (type) => {
    if (type === 'necessary') return; // Non può essere disattivato
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showConsent) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
      <div className="bg-white rounded-t-lg md:rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        
        {!showSettings ? (
          // Banner principale
          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center">
                <Cookie className="w-8 h-8 text-amber-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">
                  Utilizziamo i cookie 🍪
                </h2>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 leading-relaxed mb-4">
                Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza di navigazione, 
                analizzare il traffico del sito e personalizzare i contenuti. Puoi scegliere di accettare 
                tutti i cookie o personalizzare le tue preferenze.
              </p>
              
              <p className="text-sm text-gray-500">
                I cookie necessari sono sempre attivi per garantire il funzionamento del sito. 
                Per maggiori informazioni consulta la nostra{' '}
                <Link to="/privacy-policy" className="text-purple-600 underline hover:text-purple-700">
                  Privacy Policy
                </Link>.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAcceptAll}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center"
              >
                <Check className="w-5 h-5 mr-2" />
                Accetta tutti i cookie
              </button>
              
              <button
                onClick={handleAcceptNecessaryOnly}
                className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Solo necessari
              </button>
              
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-5 h-5 mr-2" />
                Personalizza
              </button>
            </div>
          </div>
        ) : (
          // Pannello impostazioni
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Preferenze Cookie</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Cookie necessari */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Cookie Necessari</h3>
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    Sempre attivi
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Questi cookie sono essenziali per il funzionamento del sito web e non possono essere disabilitati. 
                  Includono sessioni, sicurezza e accessibilità.
                </p>
              </div>
              
              {/* Cookie analitici */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Cookie Analitici</h3>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences.analytics ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      preferences.analytics ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Ci aiutano a capire come i visitatori interagiscono con il sito raccogliendo informazioni anonime. 
                  Include Google Analytics.
                </p>
              </div>
              
              {/* Cookie marketing */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Cookie Marketing</h3>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences.marketing ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      preferences.marketing ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Utilizzati per tracciare i visitatori sui siti web per mostrare annunci personalizzati. 
                  Include Facebook Pixel e Google Ads.
                </p>
              </div>
              
              {/* Cookie funzionali */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Cookie Funzionali</h3>
                  <button
                    onClick={() => togglePreference('functional')}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences.functional ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      preferences.functional ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Permettono funzionalità avanzate come chat dal vivo, video incorporati e altre integrazioni di terze parti.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                onClick={handleSavePreferences}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Salva Preferenze
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Accetta tutto
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;